import { generatePath } from "react-router-dom";

/**
 * Extracts the param names from a path string into a typed params object.
 *
 * `/foo/:bar/:baz` -> `{ bar: string; baz: string }`
 * `/foo` -> `Record<string, never>`
 */
type ParamsOf<P extends string> = P extends `${string}:${infer Param}/${infer Rest}`
  ? { [K in Param | keyof ParamsOf<`/${Rest}`>]: string }
  : P extends `${string}:${infer Param}`
    ? { [K in Param]: string }
    : Record<string, never>;

type SearchParams = Record<string, string | undefined>;

/**
 * Param-less routes: `url()` or `url(searchParams)`.
 * Routes with path params: `url(params)` or `url(params, searchParams)`.
 */
type UrlFn<P extends string> =
  ParamsOf<P> extends Record<string, never>
    ? (searchParams?: SearchParams) => string
    : (params: ParamsOf<P>, searchParams?: SearchParams) => string;

/**
 * Pairs a route's path string with a typed URL builder so the two can't drift.
 *
 * The same `path` feeds both the `RouteObject` in the route tree and the `url(...)` builder
 * used by callers, and `url`'s params are checked against the `:params` in the path at compile time.
 */
export function defineRoute<P extends string>(path: P) {
  const hasPathParams = path.includes(":");

  /**
   * Runtime `url` is intentionally loose (`first`/`second` + casts) so one body can
   * serve both UrlFn shapes. Callers only see UrlFn<P>: param-less routes get
   * `url()` / `url(search)`, param routes still require typed path params.
   * The casts are an implementation seam; they do not weaken the public API.
   */
  function url(first?: object, second?: SearchParams): string {
    const params = (hasPathParams ? first : {}) as ParamsOf<P>;
    const searchParams = (hasPathParams ? second : first) as SearchParams | undefined;
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(searchParams ?? {})) {
      if (value) search.set(key, value);
    }
    const pathname = generatePath(path, params);
    return search.size ? `${pathname}?${search}` : pathname;
  }

  return { path, url: url as UrlFn<P> };
}
